import React, { useState, useRef, useEffect } from 'react';
import { Modal, Card, DatePicker, Select, Button, Typography, Row, Col, Space, Popover, Input, Tooltip } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { ArrowLeftOutlined, SaveOutlined, ShopOutlined, InfoCircleOutlined, FileTextOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getTitularesTransmision, type Transmision } from '../api/transmisionesApi';
import InfoTooltip from './common/InfoTooltip';
import FarmaciaTitularesModal from './FarmaciaTitularesModal';
import { FctEstado, fmtFecha } from '../fct/react';
import { fct } from '../fct/tokens/tokens';

const { TextArea } = Input;

const { Text } = Typography;
const { Option } = Select;

interface TransmisionDetailModalProps {
  visible: boolean;
  transmision: Transmision | null;
  onClose: () => void;
  onSave: (id: number, fecha: string, estadoId: number, notasGestion?: string) => Promise<void>;
}

// Los titulares actuales salen de Gescom (#221); la ventana pide los de la farmacia, sin usuario ni opción.
const cargarTitularesGescom = (farmaciaId: number) => getTitularesTransmision(farmaciaId);

const TransmisionDetailModal: React.FC<TransmisionDetailModalProps> = ({
  visible,
  transmision,
  onClose,
  onSave,
}) => {
  const [fecha, setFecha] = useState<Dayjs | null>(null);
  const [estadoId, setEstadoId] = useState<number>(3);
  const [saving, setSaving] = useState(false);
  const [showTitularesModal, setShowTitularesModal] = useState(false);
  const [notasGestion, setNotasGestion] = useState<string>('');
  const [showNotasGestionPopup, setShowNotasGestionPopup] = useState(false);
  const notasTextAreaRef = useRef<TextAreaRef>(null);

  // Focus the textarea when popover opens
  useEffect(() => {
    if (showNotasGestionPopup && notasTextAreaRef.current) {
      // Small delay to ensure popover is rendered
      setTimeout(() => {
        notasTextAreaRef.current?.focus();
      }, 100);
    }
  }, [showNotasGestionPopup]);

  // TODO: Replace with actual user ID from authentication context
  const userId = 1;

  // Initialize state when transmision changes
  React.useEffect(() => {
    if (transmision) {
      setFecha(dayjs(transmision.fecha));
      // Normalize estadoId to valid values: 3 (Pendiente) or 5 (Realizada)
      // If estadoId is 5, keep it; otherwise default to 3 (Pendiente)
      const normalizedEstadoId = transmision.estadoId === 5 ? 5 : 3;
      setEstadoId(normalizedEstadoId);
      setNotasGestion(transmision.notasGestion || '');
    }
  }, [transmision]);

  if (!transmision) return null;

  const handleSave = async () => {
    if (!fecha) return;

    setSaving(true);
    try {
      // For "En venta" (estadoId 0), preserve the original estado
      const estadoToSave = isEnVenta ? 0 : estadoId;
      await onSave(transmision.id, fecha.format('YYYY-MM-DD'), estadoToSave, notasGestion || undefined);
      onClose();
    } catch (error) {
      console.error('Error saving transmision:', error);
    } finally {
      setSaving(false);
    }
  };

  // Check if notasGestion has content (for icon state)
  const hasNotasGestion = notasGestion.trim().length > 0;

  // Un cliente de Gescom: su nombre y, como texto, su ID; sin enlace a Gedeco (#221).
  const renderClientLink = (cliente?: { id: number; _Name: string; codigoGF?: string }) => {
    if (!cliente) return <Text type="secondary">Sin datos</Text>;

    return (
      <span>
        {cliente._Name} <Text type="secondary" className="fct-mono">({cliente.id})</Text>
      </span>
    );
  };

  const vendedores = transmision.vendedores?.filter(v => v.cliente) || [];
  const compradores = transmision.compradores?.filter(c => c.cliente) || [];

  // Detect titulares that appear in both lists (vendedores and compradores)
  const vendedorIds = new Set(vendedores.map(v => v.cliente?.id).filter(Boolean) as number[]);
  const compradorIds = new Set(compradores.map(c => c.cliente?.id).filter(Boolean) as number[]);
  const sharedTitularIds = new Set(
    Array.from(vendedorIds).filter(id => compradorIds.has(id))
  );

  const isSharedTitular = (clienteId?: number) => {
    return clienteId ? sharedTitularIds.has(clienteId) : false;
  };

  // Titular que está a la vez entre los que venden y los que compran: la condición se dice con una
  // etiqueta neutra, no con un fondo verde de marca (R-10).
  const EtiquetaCompartido = () => (
    <FctEstado personalizado={{ etiqueta: 'Compartido', familia: 'neutro', icono: 'equipo' }} />
  );

  // Check if transmission is "En venta" (read-only mode)
  const isEnVenta = transmision.estadoId === 0;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ top: 20 }}
      closable={false}
      bodyStyle={{ padding: '24px', background: fct.superficie.hundida }}
    >
      {/* Header buttons */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onClose}
          size="large"
        >
          Volver
        </Button>
        <Space>
          <Popover
            content={
              <div style={{ width: 350 }}>
                <TextArea
                  ref={notasTextAreaRef}
                  value={notasGestion}
                  onChange={(e) => setNotasGestion(e.target.value)}
                  placeholder="Escribe aquí tus notas de gestión..."
                  autoSize={{ minRows: 4, maxRows: 10 }}
                />
              </div>
            }
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Notas de gestión</span>
                <CloseOutlined
                  style={{ cursor: 'pointer', color: fct.tinta['500'] }}
                  onClick={() => setShowNotasGestionPopup(false)}
                />
              </div>
            }
            trigger="click"
            open={showNotasGestionPopup}
            onOpenChange={setShowNotasGestionPopup}
            placement="bottomRight"
          >
            <Button
              // Icono de trazo siempre (R-14): con notas se distingue por tinta y por aria-pressed.
              icon={<FileTextOutlined style={hasNotasGestion ? { color: fct.marca['verde-oscuro'] } : undefined} />}
              aria-pressed={hasNotasGestion}
              aria-label={hasNotasGestion ? 'Notas de gestión (con contenido)' : 'Notas de gestión'}
              size="large"
            />
          </Popover>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            size="large"
          >
            Guardar
          </Button>
        </Space>
      </div>

      {/* Estado selector/badge - positioned on the right */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        {isEnVenta ? (
          <FctEstado personalizado={{ etiqueta: 'En venta', familia: 'proceso' }} />
        ) : (
          <Select
            value={estadoId}
            onChange={setEstadoId}
            style={{ width: 150 }}
            size="large"
          >
            <Option value={3}>
              <FctEstado personalizado={{ etiqueta: 'Pendiente', familia: 'aviso' }} />
            </Option>
            <Option value={5}>
              <FctEstado personalizado={{ etiqueta: 'Realizada', familia: 'exito' }} />
            </Option>
          </Select>
        )}
      </div>

      {/* Main content */}
      <Row gutter={[24, 24]} align="middle" style={{ minHeight: '50vh' }}>
        {/* Left card: Titulares actuales */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <span>
                Titulares actuales
                <InfoTooltip
                  text="Titulares de la farmacia en el sistema en el momento de generar este registro de transmisión"
                  placement="topRight"
                />
              </span>
            }
            bordered
            style={{
              height: '100%',
              minHeight: 300,
              borderColor: fct.linea.tarjeta,
              boxShadow: fct.elev['1']
            }}
            headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {vendedores.length > 0 ? (
                vendedores.map((v, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: isSharedTitular(v.cliente?.id) ? fct.superficie.hundida : 'transparent',
                      border: isSharedTitular(v.cliente?.id) ? `1px solid ${fct.linea.tarjeta}` : '1px solid transparent',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap'
                    }}
                  >
                    {renderClientLink(v.cliente)}
                    {isSharedTitular(v.cliente?.id) && <EtiquetaCompartido />}
                  </div>
                ))
              ) : (
                <Text type="secondary">No hay titulares registrados</Text>
              )}
            </Space>
          </Card>
        </Col>

        {/* Center column: Farmacia + Fecha */}
        <Col xs={24} lg={8} style={{ textAlign: 'center' }}>
          {/* Farmacia card (above) */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <ShopOutlined style={{ marginRight: 8 }} />
                  Farmacia
                </span>
                <Tooltip title="Ver titulares">
                  <InfoCircleOutlined
                    aria-label="Ver titulares"
                    style={{ color: fct.interaccion.enlace, cursor: 'pointer', fontSize: 16 }}
                    onClick={() => setShowTitularesModal(true)}
                  />
                </Tooltip>
              </div>
            }
            bordered
            style={{
              marginBottom: 24,
              borderColor: fct.linea.tarjeta,
              position: 'relative',
              paddingBottom: 40,
              background: `linear-gradient(to bottom, ${fct.superficie.tarjeta} 0%, ${fct.superficie.hundida} 100%)`,
              boxShadow: fct.elev['1']
            }}
            headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Text strong>{transmision.farmacia?._Name || 'Sin datos'}</Text>
              <Text type="secondary" className="fct-mono">({transmision.farmaciaId})</Text>
            </div>
            {/* Provincia badge in bottom right corner */}
            <div style={{
              position: 'absolute',
              bottom: 8,
              right: 12,
              padding: '4px 12px',
              backgroundColor: fct.estado.neutro.relleno,
              border: `1px solid ${fct.estado.neutro.linea}`,
              borderRadius: 4,
              fontSize: '13px',
              color: fct.estado.neutro.texto
            }}>
              {transmision.farmacia?.provincia || 'N/A'}
            </div>
          </Card>

          {/* Fecha (above arrow) */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '200px' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Fecha Transmisión:
              </Text>
              <DatePicker
                value={fecha}
                onChange={(date) => setFecha(date)}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                disabled={isEnVenta}
              />
            </div>
          </div>

          {/* Arrow (below fecha) */}
          <div style={{ marginBottom: 16 }}>
            <svg width="160" height="32" viewBox="0 0 160 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 16H150M150 16L140 8M150 16L140 24" stroke={fct.tinta['500']} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </Col>

        {/* Right card: Nuevos titulares */}
        <Col xs={24} lg={8}>
          <Card
            title="Nuevos titulares"
            bordered
            style={{
              height: '100%',
              minHeight: 300,
              borderColor: fct.linea.tarjeta,
              boxShadow: fct.elev['1']
            }}
            headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {compradores.length > 0 ? (
                compradores.map((c, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: isSharedTitular(c.cliente?.id) ? fct.superficie.hundida : 'transparent',
                      border: isSharedTitular(c.cliente?.id) ? `1px solid ${fct.linea.tarjeta}` : '1px solid transparent',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap'
                    }}
                  >
                    {renderClientLink(c.cliente)}
                    {isSharedTitular(c.cliente?.id) && <EtiquetaCompartido />}
                  </div>
                ))
              ) : (
                <Text type="secondary">No hay nuevos titulares registrados</Text>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Additional info */}
      <div style={{
        marginTop: 32,
        padding: 16,
        background: fct.superficie.tarjeta,
        borderRadius: fct.radio.tarjeta,
        border: `1px solid ${fct.linea.tarjeta}`,
        boxShadow: fct.elev['1']
      }}>
        <Row gutter={[24, 16]}>
          {/* Left column: Creation info */}
          <Col span={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">ID: </Text>
                <Text className="fct-mono">{transmision.id}</Text>
              </div>
              <div>
                <Text type="secondary">Creado por: </Text>
                <Text>{transmision.responsable?.nombreAbreviado || transmision.responsable?.nombreCompleto || 'N/A'}</Text>
              </div>
              <div>
                <Text type="secondary">El día: </Text>
                <Text className="fct-mono">{fmtFecha(transmision.fechaCreacion)}</Text>
              </div>
            </Space>
          </Col>

          {/* Center column: Notes */}
          <Col span={12}>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Notas</Text>
              <Text>{transmision.notas || 'Sin notas'}</Text>
            </div>
          </Col>

          {/* Right column: Origen */}
          <Col span={6}>
            <div>
              <Text type="secondary">Origen: </Text>
              <Text>{transmision.origen || 'N/A'}</Text>
            </div>
          </Col>
        </Row>
      </div>

      {/* Titulares Modal */}
      <FarmaciaTitularesModal
        visible={showTitularesModal}
        onClose={() => setShowTitularesModal(false)}
        farmaciaId={transmision.farmaciaId}
        farmaciaName={transmision.farmacia?._Name || 'Sin datos'}
        userId={userId}
        cargarTitulares={cargarTitularesGescom}
      />
    </Modal>
  );
};

export default TransmisionDetailModal;
